import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplications } from "../../../../hooks/useApplications"
import { digitalManagerApi } from "../../../../services/api"
import {
  useApplicationSelectRenderer,
  useIssueReportTopicPriorityField,
} from "../../../components/Helpers"
import { TopicForm } from "../../../components/forms/TopicForm"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../models/Authorization"
import { IssueReportTopicPost } from "../../../models/IssueReportTopic"
import { ControllerFieldRendererProps } from "../../../types/reactHookForm"

const validationSchema = z.object({
  applicationId: z.string().min(1, "Application is required"),
  name: z.string().min(1, "Name is required"),
  context: z.string().min(1, "Context is required"),
  usefulLink: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val ||
        /^https?:\/\/(?:[\w-]{1,63}\.)*abb\.com(?:\/[^\s?#]*)?(?:\?[^\s#]*)?$/.test(val),
      {
        message: "URL must be well-formed and end with 'abb.com'",
      }
    ),
  priority: z.number().min(0, "Priority is required"),
})

export function IssueReportTopicCreate() {
  const { applicationsList, isLoadingApplications } = useApplications(
    DimaFeatureShortNameEnum.IR,
    AccessLevel.Write
  )
  const navigate = useNavigate()
  const returnURL = `/issue-reports#topics`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<IssueReportTopicPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      name: "",
      context: "",
      usefulLink: "",
      priority: 0,
    })
  }, [reset])

  const onSubmit = useCallback(
    (values: IssueReportTopicPost) =>
      digitalManagerApi
        .post(`/issueReportTopics`, values)
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Issue report topic created")
            navigate(returnURL)
          }
        })
        .catch(() => {
          toast.error("Cannot create issue report topic")
        }),
    [navigate, returnURL]
  )

  const handleApplicationSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        { applicationId: string } & IssueReportTopicPost,
        "applicationId"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderApplicationIdSelect = useApplicationSelectRenderer({
    applicationsList,
    errors,
    handleApplicationSelectChange,
    isLoadingApplications,
  })

  const handleNameFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        { applicationId: string } & IssueReportTopicPost,
        "name"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleContextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        { applicationId: string } & IssueReportTopicPost,
        "context"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleUsefulLinkFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        { applicationId: string } & IssueReportTopicPost,
        "usefulLink"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldName = useCallback(
    ({
      field,
    }: ControllerFieldRendererProps<
      { applicationId: string } & IssueReportTopicPost,
      "name"
    >) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameFieldChange]
  )

  const renderTextFieldContext = useCallback(
    ({
      field,
    }: ControllerFieldRendererProps<
      { applicationId: string } & IssueReportTopicPost,
      "context"
    >) => (
      <MUITextField
        id="context"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleContextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.context?.message === "string"
            ? { context: { message: errors.context.message } }
            : undefined
        }
      />
    ),
    [errors.context, handleContextFieldChange]
  )

  const renderTextFieldUsefulLink = useCallback(
    ({
      field,
    }: ControllerFieldRendererProps<
      { applicationId: string } & IssueReportTopicPost,
      "usefulLink"
    >) => (
      <MUITextField
        id="usefulLink"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleUsefulLinkFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.usefulLink?.message === "string"
            ? { usefulLink: { message: errors.usefulLink.message } }
            : undefined
        }
      />
    ),
    [errors.usefulLink, handleUsefulLinkFieldChange]
  )

  const renderPriorityTextField = useIssueReportTopicPriorityField<IssueReportTopicPost>({
    errors,
  })

  return (
    <TopicForm
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
      includeContext
      includeUsefulLink
      includePriority
      control={control}
      errors={errors}
      renderApplicationSelect={renderApplicationIdSelect}
      renderNameField={renderTextFieldName}
      renderContextField={renderTextFieldContext}
      renderUsefulLinkField={renderTextFieldUsefulLink}
      renderPriorityField={renderPriorityTextField}
    />
  )
}
